import { useEffect, useMemo, useState } from 'react';

import { getLogtoRolesFromAccessToken } from '@/lib/logto';
import {
  recheckLogtoRoles,
  roleRevalidation,
  watchRoleRevalidationTriggers,
} from '@/lib/roleRevalidation';
import { useAppSelector } from '@/store/hooks';

/**
 * I ruoli Logto dell'utente corrente, **riletti quando il token cambia**.
 *
 * 🔑 PERCHE' ESISTE, invece di lasciare la sottoscrizione dentro `useHasLogtoRole`.
 * `AdminGate` non chiede «ho QUESTO ruolo?»: chiede «ho ALMENO UNO fra `administrator` e
 * `beta`?». Farglielo fare con due `useHasLogtoRole` significherebbe due sottoscrizioni per
 * la stessa domanda; riscrivergli la sottoscrizione addosso significherebbe **una seconda
 * copia della logica di rivalutazione** — e due copie divergono, sempre, e la seconda
 * diverge in silenzio perche' nessuno la guarda. Lo store resta uno solo
 * (`lib/roleRevalidation.ts`, #164) e questo hook e' l'unico punto in cui React ci si
 * abbona.
 *
 * ⚠️ **Ritorna `[]` per l'utente non autenticato**, non i ruoli di un token rimasto in
 * `localStorage`: `bridgeAuthenticated` e' la fonte di verita' del gate d'autenticazione, e
 * leggere i ruoli senza guardarlo darebbe privilegi a una sessione che l'app considera
 * chiusa.
 *
 * ⚠️ **La rivalutazione e' SIMMETRICA**: un token rinnovato senza un ruolo lo toglie durante
 * la sessione. Corretto — un gate che ricordasse un privilegio revocato sarebbe peggio.
 */
export function useLogtoRoles(): readonly string[] {
  const authed = useAppSelector((s) => s.auth.bridgeAuthenticated);
  const [versione, setVersione] = useState(() => roleRevalidation.version());

  useEffect(() => {
    // Due iscrizioni, e sono due cose diverse: allo store (che il bottone «Ricontrolla»
    // sveglia direttamente) e agli eventi che lo svegliano da soli.
    const stop = roleRevalidation.subscribe(() => setVersione(roleRevalidation.version()));
    const unwatch = watchRoleRevalidationTriggers(
      window,
      () => document.visibilityState === 'visible',
      recheckLogtoRoles,
    );
    return () => {
      stop();
      unwatch();
    };
  }, []);

  // `versione` entra nelle dipendenze e non e' usata dentro: e' proprio il suo mestiere —
  // e' il segnale «il token potrebbe essere cambiato, rileggi». Toglierla dall'elenco non
  // produce nessun errore e riporta il difetto misurato il 2026-09-06.
  return useMemo(() => {
    if (!authed) return [];
    return getLogtoRolesFromAccessToken();
  }, [authed, versione]);
}
